import type { MasteryId, NodeType } from '../config/masteries'
import { masteryCategories, allMasteries, masteryXpNeeded, nodeType, nodeCost, previewMasteryGain, getMasteryCategoryLabel, getMasteryLabel, getMasteryTreeLabel, nodeToCode, codeToNode } from '../config/masteries'
import type { MasteryDef, MasteryTreeDef } from '../config/masteries'
import { getNodeDescription, nodeHasAnyEffect, MASTERY_DUMP, getMasteryDumpLabel } from '../config/mastery-nodes'
import type { MasteryProgress } from '../core/character'
import { masteryPointsAvailable, defaultMasteryNodes, masteryHistoryComplete } from '../core/character'
import { linkifyNoteTerms, mountNoteModal } from './notes'
import { t } from '../i18n'
import { playSound } from '../audio'
import { getPrefs, setPref } from '../core/prefs'

// ── Helpers ────────────────────────────────────────────────────────────────

// Renders the mastery progress bar with min-bar semantics:
// - 0 amount → empty (just the back-track)
// - any amount > 0 → at least a min-bar (caps clamped via CSS min-width)
// - both > 0 → the CSS :has() rule strips inner caps so yellow keeps only its
//   opening cap and green only its closing cap, each at 9px min-width.
// Both fills are absolutely positioned at the same percentage boundary so the
// yellow→green seam is exact and never leaks the back-track between them.
export function renderMasteryBar(oldPct: number, gainPct: number): string {
  const hasOld = oldPct > 0
  const hasGain = gainPct > 0
  const oldDiv = hasOld
    ? `<div class="mastery-bar-old" style="width:${oldPct}%"></div>`
    : ''
  // In paired mode the yellow fill has min-width: 9px, so its visual right
  // edge can exceed oldPct% when that percentage maps to fewer than 9px.
  // Position the green at max(oldPct%, 9px) so it never overlaps the yellow.
  const newLeft = hasOld && hasGain ? `max(${oldPct}%, 9px)` : `${oldPct}%`
  const newDiv = hasGain
    ? `<div class="mastery-bar-new" style="width:${gainPct}%;left:${newLeft}"></div>`
    : ''
  return `<div class="mastery-bar">${oldDiv}${newDiv}</div>`
}

function prog(
  masteryProgress: Partial<Record<MasteryId, MasteryProgress>>,
  id: MasteryId,
): MasteryProgress {
  const p = masteryProgress[id]
  if (p) return p
  return { xp: 0, level: 1, nodes: defaultMasteryNodes() }
}

function isNodeAssigned(p: MasteryProgress, treeIdx: number, nodeIdx: number): boolean {
  return (p.nodes[treeIdx] ?? []).includes(nodeIdx)
}

// Information about whether/how a node can be assigned.
//   assigned     — already taken
//   unavailable  — structural block (sibling key already chosen, or short-tree key 14/15)
//   assignable   — can be assigned now; `path` is the full ordered list of unassigned
//                  nodes to assign (the requested node is always last).
//   insufficient — path is computable but cost > totalAvailable
type AssignInfo =
  | { kind: 'assigned' }
  | { kind: 'unavailable'; reason: 'sibling' | 'shortKey' | 'undefined' }
  | { kind: 'assignable'; path: number[]; cost: number }
  | { kind: 'insufficient'; path: number[]; cost: number; available: number }

// Returns the ordered list of unassigned nodes that would be assigned to reach
// (and include) `nodeIdx`. Returns null with a structural-error tag when the
// node can never be reached from this state (sibling key already chosen, etc).
function computeAssignPath(
  p: MasteryProgress,
  treeDef: MasteryTreeDef,
  treeIdx: number,
  nodeIdx: number,
): number[] | { error: 'sibling' | 'shortKey' } {
  if (isNodeAssigned(p, treeIdx, nodeIdx)) return []
  const assigned = new Set(p.nodes[treeIdx] ?? [])
  const path: number[] = []
  if (nodeIdx < 12) {
    for (let i = 0; i <= nodeIdx; i++) {
      if (!assigned.has(i)) path.push(i)
    }
    return path
  }
  const majorLineIdx = nodeIdx <= 13 ? 5 : 11
  if (treeDef.short && majorLineIdx === 11) return { error: 'shortKey' }
  const siblingIdx = nodeIdx % 2 === 0 ? nodeIdx + 1 : nodeIdx - 1
  if (assigned.has(siblingIdx)) return { error: 'sibling' }
  for (let i = 0; i <= majorLineIdx; i++) {
    if (!assigned.has(i)) path.push(i)
  }
  path.push(nodeIdx)
  return path
}

function computeAssignInfo(
  p: MasteryProgress,
  treeDef: MasteryTreeDef,
  treeIdx: number,
  nodeIdx: number,
  totalAvailable: number,
): AssignInfo {
  if (isNodeAssigned(p, treeIdx, nodeIdx)) return { kind: 'assigned' }
  const result = computeAssignPath(p, treeDef, treeIdx, nodeIdx)
  if (!Array.isArray(result)) return { kind: 'unavailable', reason: result.error }
  if (result.length === 0) return { kind: 'assigned' }
  const cost = result.reduce((sum, idx) => sum + nodeCost(idx), 0)
  if (cost <= totalAvailable) return { kind: 'assignable', path: result, cost }
  return { kind: 'insufficient', path: result, cost, available: totalAvailable }
}

// ── Reset Confirmation Modal ───────────────────────────────────────────────

function mountResetConfirmModal(
  parent: HTMLElement,
  masteryLabel: string,
  onConfirm: () => void,
  onClose: () => void,
  description = 'All assigned nodes will be cleared and you will lose 1 level in this mastery.',
): () => void {
  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop mastery-node-backdrop'
  backdrop.innerHTML = `
    <div class="modal-panel mastery-node-panel" role="dialog" aria-modal="true" aria-labelledby="reset-confirm-title">
      <button class="modal-close-btn" data-action="close" aria-label="${t('settings', 'close')}"></button>
      <h2 class="modal-title" id="reset-confirm-title">${t('mastery', 'resetTitle').replace('{name}', masteryLabel)}</h2>
      <p class="node-detail-desc">${description}</p>
      <div class="node-detail-actions reset-confirm-actions">
        <button class="modal-btn modal-btn--ghost" data-action="cancel" data-sfx="modal">${t('mastery', 'confirmCancel')}</button>
        <button class="modal-btn modal-btn--danger" data-action="confirm" data-sfx="modal">${t('mastery', 'confirmReset')}</button>
      </div>
    </div>
  `
  playSound('modal.open')
  parent.appendChild(backdrop)

  const dismiss = (): void => { playSound('modal.close'); backdrop.remove(); onClose() }
  backdrop.querySelector<HTMLButtonElement>('[data-action="close"]')!.addEventListener('click', dismiss)
  backdrop.querySelector<HTMLButtonElement>('[data-action="cancel"]')!.addEventListener('click', dismiss)
  backdrop.addEventListener('click', e => { if (e.target === backdrop) dismiss() })
  backdrop.querySelector<HTMLButtonElement>('[data-action="confirm"]')!.addEventListener('click', () => {
    onConfirm()
    dismiss()
  })

  return () => backdrop.remove()
}

// ── Node Detail Modal ──────────────────────────────────────────────────────

function mountNodeDetailModal(
  parent: HTMLElement,
  info: {
    masteryId: MasteryId
    treeIdx: number
    treeLabel: string
    nodeIdx: number
    desc: string
    assignInfo: AssignInfo
  },
  onAssign: () => void,
  onClose: () => void,
): () => void {
  const typeLabelMap: Record<string, 'nodeSmall' | 'nodeStrong' | 'nodeMajor' | 'nodeKey'> = { small: 'nodeSmall', strong: 'nodeStrong', major: 'nodeMajor', key: 'nodeKey' }
  const typeLabel = t('mastery', typeLabelMap[nodeType(info.nodeIdx)])

  let actionHtml = ''
  if (info.assignInfo.kind === 'assigned') {
    actionHtml = `<span class="node-detail-assigned">${t('mastery', 'nodeAssigned')}</span>`
  } else if (info.assignInfo.kind === 'assignable') {
    const cost = info.assignInfo.cost
    const label = (cost === 1 ? t('mastery', 'nodeAssignPt') : t('mastery', 'nodeAssignPts')).replace('{cost}', String(cost))
    actionHtml = `<button class="modal-btn modal-btn--primary node-detail-assign-btn" data-action="assign" data-sfx="modal">${label}</button>`
  } else if (info.assignInfo.kind === 'insufficient') {
    const { cost, available } = info.assignInfo
    const tpl = cost === 1 ? t('mastery', 'nodeNeedPt') : t('mastery', 'nodeNeedPts')
    actionHtml = `<span class="node-detail-blocked">${tpl.replace('{cost}', String(cost)).replace('{available}', String(available))}</span>`
  } else {
    actionHtml = info.assignInfo.reason === 'sibling'
      ? `<span class="node-detail-blocked">${t('mastery', 'nodeKeyChosen')}</span>`
      : info.assignInfo.reason === 'undefined'
      ? `<span class="node-detail-blocked">${t('mastery', 'nodeNotImpl')}</span>`
      : `<span class="node-detail-blocked">${t('mastery', 'nodeNotAvail')}</span>`
  }

  const descHtml = linkifyNoteTerms(info.desc)
  const code = nodeToCode(info.masteryId, info.treeIdx, info.nodeIdx)
  const codeBadge = code ? `<span class="node-code-badge">${code}</span>` : ''

  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop mastery-node-backdrop'
  backdrop.innerHTML = `
    <div class="modal-panel mastery-node-panel" role="dialog" aria-modal="true" aria-labelledby="node-detail-title">
      <button class="modal-close-btn" data-action="close" aria-label="${t('settings', 'close')}"></button>
      ${codeBadge}
      <h2 class="modal-title" id="node-detail-title">${typeLabel}</h2>
      <p class="node-detail-tree">${info.treeLabel}</p>
      <p class="node-detail-desc">${descHtml}</p>
      <div class="node-detail-actions">${actionHtml}</div>
    </div>
  `
  playSound('modal.open')
  parent.appendChild(backdrop)

  let noteCleanup: (() => void) | null = null
  const closeNote = (): void => { if (noteCleanup) { noteCleanup(); noteCleanup = null } }

  backdrop.addEventListener('click', (e) => {
    const link = (e.target as HTMLElement).closest<HTMLElement>('[data-note-id]')
    if (link) {
      const noteId = link.dataset.noteId!
      e.stopPropagation()
      closeNote()
      noteCleanup = mountNoteModal(parent, noteId, () => { noteCleanup = null })
      return
    }
    if (e.target === backdrop) dismiss()
  })

  const dismiss = (): void => { playSound('modal.close'); closeNote(); backdrop.remove(); onClose() }
  backdrop.querySelector<HTMLButtonElement>('[data-action="close"]')!.addEventListener('click', dismiss)
  backdrop.querySelector<HTMLButtonElement>('[data-action="assign"]')?.addEventListener('click', () => {
    onAssign()
    dismiss()
  })

  return () => { closeNote(); backdrop.remove() }
}

// ── Tree Row Builder ───────────────────────────────────────────────────────

// Half-width (and half-height) of a node by index — used to extend bars
// center-to-center so the bar's ends sit hidden under the adjacent nodes.
function nodeHalfSize(nodeIdx: number): number {
  switch (nodeType(nodeIdx)) {
    case 'small':  return 12  // 24/2
    case 'strong': return 22  // 44/2
    case 'major':  return 36  // 72/2
    case 'key':    return 22  // 44/2
  }
}

const H_BAR_GAP = 20  // distance between adjacent node edges
const V_BAR_GAP = 16  // distance between major and key edges

function mkNode(treeIdx: number, nodeIdx: number, type: NodeType, assigned: boolean, unavailable = false): HTMLElement {
  const node = document.createElement('div')
  let cls = `tree-node tree-node--${type}`
  if (assigned) cls += ' tree-node--assigned'
  else if (unavailable) cls += ' tree-node--unavailable'
  node.className = cls
  node.dataset['tree'] = String(treeIdx)
  node.dataset['node'] = String(nodeIdx)
  node.setAttribute('role', 'button')
  node.setAttribute('tabindex', '0')
  node.dataset['sfx'] = 'modal'
  node.textContent = unavailable && !assigned ? '×' : '+'
  return node
}

function buildTreeNodes(
  def: MasteryDef,
  treeDef: MasteryTreeDef,
  p: MasteryProgress,
  treeIdx: number,
  freePointsUsedHere: number,
  remainingFreePoints: number,
  dumpedHere: number,
): HTMLElement {
  const container = document.createElement('div')
  container.className = 'mastery-tree-nodes'
  const totalAvailable = masteryPointsAvailable(p, freePointsUsedHere, dumpedHere) + remainingFreePoints

  // Short trees end after the first major (line nodes 0-5; key nodes 12-13 only).
  const lastLineIdx = treeDef.short ? 5 : 11

  // Build line elements, inserting bars and major clusters at the right spots
  for (let lineIdx = 0; lineIdx <= lastLineIdx; lineIdx++) {
    const type = nodeType(lineIdx)

    if (type === 'major') {
      // Build the cluster: key-A / vbar / major / vbar / key-B + hbar to the right (unless last)
      const keyAIdx = lineIdx === 5 ? 12 : 14
      const keyBIdx = lineIdx === 5 ? 13 : 15
      const majorAssigned = isNodeAssigned(p, treeIdx, lineIdx)
      const keyAAssigned = isNodeAssigned(p, treeIdx, keyAIdx)
      const keyBAssigned = isNodeAssigned(p, treeIdx, keyBIdx)

      // Skip rendering a key (and its connecting bar) when it has no effect defined.
      // Falls back gracefully if a key was somehow assigned despite having no effect.
      const showKeyA = nodeHasAnyEffect(def.id, treeIdx, keyAIdx) || keyAAssigned
      const showKeyB = nodeHasAnyEffect(def.id, treeIdx, keyBIdx) || keyBAssigned

      const infoA = computeAssignInfo(p, treeDef, treeIdx, keyAIdx, totalAvailable)
      const infoB = computeAssignInfo(p, treeDef, treeIdx, keyBIdx, totalAvailable)
      const keyAUnavailable = !keyAAssigned && infoA.kind === 'unavailable'
      const keyBUnavailable = !keyBAssigned && infoB.kind === 'unavailable'

      const cluster = document.createElement('div')
      cluster.className = 'tree-major-cluster'

      const keyHalf = nodeHalfSize(keyAIdx)
      const majorHalf = nodeHalfSize(lineIdx)

      if (showKeyA) {
        const keyA = mkNode(treeIdx, keyAIdx, 'key', keyAAssigned, keyAUnavailable)
        const vBarA = document.createElement('div')
        const vBarAFilled = majorAssigned && keyAAssigned
        vBarA.className = `tree-bar--v${vBarAFilled ? ' tree-bar--filled' : ''}`
        vBarA.dataset['barFrom'] = String(keyAIdx)
        vBarA.dataset['barTo'] = String(lineIdx)
        vBarA.style.height = `${keyHalf + V_BAR_GAP + majorHalf}px`
        vBarA.style.marginTop = `-${keyHalf}px`
        vBarA.style.marginBottom = `-${majorHalf}px`
        cluster.append(keyA, vBarA)
      }

      cluster.append(mkNode(treeIdx, lineIdx, 'major', majorAssigned))

      if (showKeyB) {
        const vBarB = document.createElement('div')
        const vBarBFilled = majorAssigned && keyBAssigned
        vBarB.className = `tree-bar--v${vBarBFilled ? ' tree-bar--filled' : ''}`
        vBarB.dataset['barFrom'] = String(lineIdx)
        vBarB.dataset['barTo'] = String(keyBIdx)
        vBarB.style.height = `${majorHalf + V_BAR_GAP + keyHalf}px`
        vBarB.style.marginTop = `-${majorHalf}px`
        vBarB.style.marginBottom = `-${keyHalf}px`
        const keyB = mkNode(treeIdx, keyBIdx, 'key', keyBAssigned, keyBUnavailable)
        cluster.append(vBarB, keyB)
      }

      container.appendChild(cluster)
    } else {
      container.appendChild(mkNode(treeIdx, lineIdx, type, isNodeAssigned(p, treeIdx, lineIdx)))
    }

    // Add h-bar between this node and the next (not after the last). A bar is
    // filled (yellow) only when BOTH adjacent line nodes are assigned.
    if (lineIdx < lastLineIdx) {
      const leftHalf = nodeHalfSize(lineIdx)
      const rightHalf = nodeHalfSize(lineIdx + 1)
      const filled = isNodeAssigned(p, treeIdx, lineIdx) && isNodeAssigned(p, treeIdx, lineIdx + 1)
      const bar = document.createElement('div')
      bar.className = `tree-bar--h${filled ? ' tree-bar--filled' : ''}`
      bar.dataset['barFrom'] = String(lineIdx)
      bar.dataset['barTo'] = String(lineIdx + 1)
      bar.style.width = `${leftHalf + H_BAR_GAP + rightHalf}px`
      bar.style.marginLeft = `-${leftHalf}px`
      bar.style.marginRight = `-${rightHalf}px`
      container.appendChild(bar)
    }
  }

  // Hover preview: when hovering an unassigned node, color the bars along the
  // assignment path green up to whatever the user can afford right now.
  function applyPreviewFor(nodeIdx: number): void {
    const info = computeAssignInfo(p, treeDef, treeIdx, nodeIdx, totalAvailable)
    if (info.kind !== 'assignable' && info.kind !== 'insufficient') return
    // Accumulate nodes from the path as long as the budget covers each one's cost.
    let budget = totalAvailable
    const reachable: number[] = []
    for (const nIdx of info.path) {
      const c = nodeCost(nIdx)
      if (c > budget) break
      budget -= c
      reachable.push(nIdx)
    }
    if (reachable.length === 0) return
    const reachableSet = new Set(reachable)
    container.querySelectorAll<HTMLElement>('[data-bar-from]').forEach(bar => {
      const from = parseInt(bar.dataset['barFrom']!, 10)
      const to = parseInt(bar.dataset['barTo']!, 10)
      const fromAssigned = isNodeAssigned(p, treeIdx, from)
      const toAssigned = isNodeAssigned(p, treeIdx, to)
      const fromInReach = fromAssigned || reachableSet.has(from)
      const toInReach = toAssigned || reachableSet.has(to)
      if (fromInReach && toInReach && !(fromAssigned && toAssigned)) {
        bar.classList.add('tree-bar--preview')
      }
    })
  }
  function clearPreview(): void {
    container.querySelectorAll<HTMLElement>('.tree-bar--preview').forEach(b => b.classList.remove('tree-bar--preview'))
  }

  // Wire click handlers and hover preview on all tree-node elements
  container.querySelectorAll<HTMLElement>('.tree-node').forEach(nodeEl => {
    const nodeIdx = parseInt(nodeEl.dataset['node']!, 10)
    const handleActivate = (): void => {
      const info = computeAssignInfo(p, treeDef, treeIdx, nodeIdx, totalAvailable)
      const isUndefinedKey = nodeType(nodeIdx) === 'key'
        && !isNodeAssigned(p, treeIdx, nodeIdx)
        && !nodeHasAnyEffect(def.id, treeIdx, nodeIdx)
      const effectiveInfo: AssignInfo = isUndefinedKey
        ? { kind: 'unavailable', reason: 'undefined' }
        : info
      nodeEl.dispatchEvent(new CustomEvent('node-detail', {
        bubbles: true,
        detail: {
          treeIdx,
          nodeIdx,
          treeLabel: getMasteryTreeLabel(def.id, treeDef.index),
          desc: getNodeDescription(def.id, treeIdx, nodeIdx, getMasteryTreeLabel(def.id, treeDef.index)),
          info: effectiveInfo,
        },
      }))
    }
    // Ctrl/Cmd + click: bulk-assign the affordable prefix of the path
    const handleBulkAssign = (): boolean => {
      const info = computeAssignInfo(p, treeDef, treeIdx, nodeIdx, totalAvailable)
      if (info.kind !== 'assignable' && info.kind !== 'insufficient') return false
      const isUndefinedKey = nodeType(nodeIdx) === 'key' && !nodeHasAnyEffect(def.id, treeIdx, nodeIdx)
      if (isUndefinedKey) return false
      let budget = totalAvailable
      const toAssign: number[] = []
      for (const nIdx of info.path) {
        const c = nodeCost(nIdx)
        if (c > budget) break
        budget -= c
        toAssign.push(nIdx)
      }
      if (toAssign.length === 0) return false
      nodeEl.dispatchEvent(new CustomEvent('node-bulk-assign', {
        bubbles: true,
        detail: { treeIdx, nodeIndices: toAssign },
      }))
      return true
    }
    nodeEl.addEventListener('click', e => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault()
        if (handleBulkAssign()) return
      }
      handleActivate()
    })
    nodeEl.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        if ((e.ctrlKey || e.metaKey) && handleBulkAssign()) return
        handleActivate()
      }
    })
    if (!isNodeAssigned(p, treeIdx, nodeIdx)) {
      nodeEl.addEventListener('mouseenter', () => applyPreviewFor(nodeIdx))
      nodeEl.addEventListener('mouseleave', clearPreview)
      nodeEl.addEventListener('focus', () => applyPreviewFor(nodeIdx))
      nodeEl.addEventListener('blur', clearPreview)
    }
  })

  return container
}

// ── Mastery Tree Modal ─────────────────────────────────────────────────────

function mountMasteryTreeModal(
  parent: HTMLElement,
  def: MasteryDef,
  masteryProgress: Partial<Record<MasteryId, MasteryProgress>>,
  freeMasteryPointsUsed: Partial<Record<MasteryId, number>>,
  masteryDumpPoints: Partial<Record<MasteryId, number>>,
  getRemainingFreePoints: () => number,
  ascentCount: number,
  onAssign: (treeIdx: number, nodeIdx: number) => void,
  onDump: (count: number) => void,
  onReset: () => void,
  onClose: () => void,
): () => void {
  const p = prog(masteryProgress, def.id)
  const visibleTrees = def.trees.filter(t => !t.unlockAscent || ascentCount >= t.unlockAscent)

  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop mastery-tree-backdrop'

  const panel = document.createElement('div')
  panel.className = 'modal-panel mastery-tree-panel'
  panel.setAttribute('role', 'dialog')
  panel.setAttribute('aria-modal', 'true')
  panel.setAttribute('aria-labelledby', 'tree-modal-title')
  const dumpInfo = MASTERY_DUMP[def.id]
  const fmtRate = (n: number): string => Number.isInteger(n) ? String(n) : n.toFixed(1)

  panel.innerHTML = `
    <button class="modal-close-btn" data-action="close" aria-label="${t('settings', 'close')}"></button>
    <h2 class="modal-title" id="tree-modal-title">${getMasteryLabel(def.id)}</h2>
    <p class="mastery-tree-points"></p>
    <p class="mastery-tree-hint">${t('mastery', 'ctrlClickHint')}</p>
    <div class="mastery-trees-list"></div>
    <div class="mastery-tree-footer">
      <div class="mastery-dump">
        <span class="mastery-dump-label"></span>
        <button class="modal-btn modal-btn--ghost mastery-dump-btn" data-action="dump" title="${t('mastery', 'dumpTooltip')}"></button>
      </div>
      <button class="modal-btn modal-btn--danger mastery-reset-btn" data-action="reset" data-sfx="modal">${t('mastery', 'resetBtn')}</button>
    </div>
  `
  backdrop.appendChild(panel)

  const pointsEl = panel.querySelector<HTMLElement>('.mastery-tree-points')!
  const list = panel.querySelector<HTMLElement>('.mastery-trees-list')!
  const resetBtn = panel.querySelector<HTMLButtonElement>('.mastery-reset-btn')!
  const dumpLabelEl = panel.querySelector<HTMLElement>('.mastery-dump-label')!
  const dumpBtn = panel.querySelector<HTMLButtonElement>('.mastery-dump-btn')!

  const getFreeUsedHere = (): number => freeMasteryPointsUsed[def.id] ?? 0
  const getDumpedHere = (): number => masteryDumpPoints[def.id] ?? 0

  function updatePointsSummary(): void {
    const freshP = prog(masteryProgress, def.id)
    const freeUsed = getFreeUsedHere()
    const dumped = getDumpedHere()
    const remaining = getRemainingFreePoints()
    const levelAvail = masteryPointsAvailable(freshP, freeUsed, dumped)
    const total = levelAvail + remaining
    const earned = Math.max(0, freshP.level - 1) + freeUsed + remaining
    const freeCount = freeUsed + remaining
    const freePart = freeCount > 0 ? t('mastery', 'pointsFree').replace('{n}', String(freeCount)) : ''
    const pointsTpl = earned !== 1 ? t('mastery', 'pointsPts') : t('mastery', 'pointsPt')
    pointsEl.textContent = pointsTpl.replace('{total}', String(total)).replace('{earned}', String(earned)).replace('{free}', freePart)
    const dumpBonus = dumped * dumpInfo.rate
    const bonusStr = fmtRate(dumpBonus)
    if (dumped > 0) {
      const dumpTpl = dumped === 1 ? t('mastery', 'dumpActivePt') : t('mastery', 'dumpActivePts')
      dumpLabelEl.innerHTML = dumpTpl.replace('{dumped}', `<strong>${dumped}</strong>`).replace('{bonus}', bonusStr).replace('{label}', getMasteryDumpLabel(def.id))
    } else {
      dumpLabelEl.innerHTML = t('mastery', 'dumpLabel').replace('{rate}', fmtRate(dumpInfo.rate)).replace('{label}', getMasteryDumpLabel(def.id))
    }
    dumpBtn.textContent = t('mastery', 'dumpBtn')
    dumpBtn.disabled = total <= 0
    resetBtn.disabled = freshP.level <= 1 && freeUsed === 0 && dumped === 0
  }

  updatePointsSummary()

  visibleTrees.forEach((treeDef) => {
    const entry = document.createElement('div')
    entry.className = 'mastery-tree-entry'
    const label = document.createElement('span')
    label.className = 'mastery-tree-label'
    label.textContent = getMasteryTreeLabel(def.id, treeDef.index)
    entry.appendChild(label)
    entry.appendChild(buildTreeNodes(def, treeDef, p, treeDef.index, getFreeUsedHere(), getRemainingFreePoints(), getDumpedHere()))
    list.appendChild(entry)
  })

  playSound('modal.open')
  parent.appendChild(backdrop)

  let subCleanup: (() => void) | null = null
  function closeSub(): void { if (subCleanup) { subCleanup(); subCleanup = null } }

  // Handle node-detail events bubbled from tree nodes
  panel.addEventListener('node-detail', (e: Event) => {
    const detail = (e as CustomEvent).detail as {
      treeIdx: number; nodeIdx: number; treeLabel: string; desc: string
      info: AssignInfo
    }
    closeSub()
    subCleanup = mountNodeDetailModal(
      parent,
      { masteryId: def.id, treeIdx: detail.treeIdx, treeLabel: detail.treeLabel, nodeIdx: detail.nodeIdx, desc: detail.desc, assignInfo: detail.info },
      () => {
        if (detail.info.kind === 'assignable') {
          for (const nIdx of detail.info.path) onAssign(detail.treeIdx, nIdx)
        }
        subCleanup = null
        rebuildTrees()
      },
      () => { subCleanup = null },
    )
  })

  // Ctrl/Cmd + click: assign affordable nodes immediately, no modal
  panel.addEventListener('node-bulk-assign', (e: Event) => {
    const detail = (e as CustomEvent).detail as { treeIdx: number; nodeIndices: number[] }
    for (const nIdx of detail.nodeIndices) onAssign(detail.treeIdx, nIdx)
    rebuildTrees()
  })

  function rebuildTrees(): void {
    const freshP = prog(masteryProgress, def.id)
    list.innerHTML = ''
    visibleTrees.forEach((treeDef) => {
      const entry = document.createElement('div')
      entry.className = 'mastery-tree-entry'
      const labelEl = document.createElement('span')
      labelEl.className = 'mastery-tree-label'
      labelEl.textContent = getMasteryTreeLabel(def.id, treeDef.index)
      entry.appendChild(labelEl)
      entry.appendChild(buildTreeNodes(def, treeDef, freshP, treeDef.index, getFreeUsedHere(), getRemainingFreePoints(), getDumpedHere()))
      list.appendChild(entry)
    })
    updatePointsSummary()
  }

  resetBtn.addEventListener('click', () => {
    closeSub()
    const freshP = prog(masteryProgress, def.id)
    const willLoseLevel = freshP.level > 1
    const resetDesc = willLoseLevel
      ? t('mastery', 'resetDescLoseLevel')
      : t('mastery', 'resetDescNoLoseLevel')
    subCleanup = mountResetConfirmModal(
      parent,
      def.label,
      () => {
        onReset()
        subCleanup = null
        rebuildTrees()
      },
      () => { subCleanup = null },
      resetDesc,
    )
  })

  dumpBtn.addEventListener('click', (e) => {
    const freshP = prog(masteryProgress, def.id)
    const available = masteryPointsAvailable(freshP, getFreeUsedHere(), getDumpedHere()) + getRemainingFreePoints()
    if (available <= 0) return
    const count = (e.ctrlKey || e.metaKey) ? available : 1
    onDump(count)
    rebuildTrees()
  })

  const dismiss = (): void => { playSound('modal.close'); closeSub(); backdrop.remove(); onClose() }
  panel.querySelector<HTMLButtonElement>('[data-action="close"]')!.addEventListener('click', dismiss)
  backdrop.addEventListener('click', e => { if (e.target === backdrop) dismiss() })

  return () => { closeSub(); backdrop.remove() }
}

// ── Build plan helpers ─────────────────────────────────────────────────────

// Canonical mastery order for save-string generation. Derived from allMasteries
// (category/display order) so every mastery — including ones added later, like
// cold and rot — is always emitted. A hardcoded list here silently dropped any
// mastery it forgot to mention.
const CANONICAL_MASTERY_ORDER: MasteryId[] = allMasteries.map(m => m.id)

// Generates a save string from the current mastery state, replaying each
// mastery's nodeHistory so the exact assignment order — interleaved across
// that mastery's trees (e.g. FD1, FB1, FD2) — is preserved. Masteries are
// independent, so they're emitted in canonical letter order.
// Returns null when any mastery with assigned nodes lacks a complete history
// (nodes assigned before nodeHistory existed): the real order is unknowable,
// so saving a misleading plan is blocked until the next ascent wipes it.
export function generateMasterySaveString(
  masteryProgress: Partial<Record<MasteryId, MasteryProgress>>,
): string | null {
  const codes: string[] = []
  for (const masteryId of CANONICAL_MASTERY_ORDER) {
    const p = masteryProgress[masteryId]
    if (!p) continue
    if (p.nodes.every(t => t.length === 0)) continue
    if (!masteryHistoryComplete(p)) return null
    for (const [treeIdx, nodeIdx] of p.nodeHistory!) {
      const code = nodeToCode(masteryId, treeIdx, nodeIdx)
      if (code) codes.push(code)
    }
  }
  return codes.join(', ')
}

// Parses a save string into a per-mastery ordered list of {treeIdx, nodeIdx}.
// Returns null if the string has no valid tokens.
export function parseMasterySaveString(
  str: string,
): Map<MasteryId, Array<{ treeIdx: number; nodeIdx: number }>> | null {
  const tokens = str.split(/,\s*/).map(s => s.trim()).filter(Boolean)
  const result = new Map<MasteryId, Array<{ treeIdx: number; nodeIdx: number }>>()
  let anyValid = false
  for (const token of tokens) {
    const parsed = codeToNode(token)
    if (!parsed) continue
    anyValid = true
    const list = result.get(parsed.masteryId) ?? []
    list.push({ treeIdx: parsed.treeIdx, nodeIdx: parsed.nodeIdx })
    result.set(parsed.masteryId, list)
  }
  return anyValid ? result : null
}

// Truncate plan string for display.
function truncatePlan(str: string, maxLen = 28): string {
  return str.length <= maxLen ? str : str.slice(0, maxLen - 1) + '…'
}

// ── Preset Save Modal ──────────────────────────────────────────────────────

export function mountMasteryPresetSaveModal(
  parent: HTMLElement,
  // null = assignment order unknown (legacy nodes without history) — saving
  // is blocked with an explanation instead of writing a misleading plan.
  currentPlan: string | null,
  onClose: () => void,
): () => void {
  const prefs = getPrefs()
  const presets: [string?, string?, string?] = [...(prefs.masteryPresets ?? [undefined, undefined, undefined])] as [string?, string?, string?]
  const blocked = currentPlan === null

  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop mastery-node-backdrop'

  function buildHtml(): string {
    const disabledAttr = blocked ? ' disabled' : ''
    const slots = ([0, 1, 2] as const).map(i => {
      const saved = presets[i]
      const label = t('mastery', 'slot').replace('{n}', String(i + 1))
      const preview = saved ? `<span class="mastery-preset-slot-preview">${truncatePlan(saved)}</span>` : `<span class="mastery-preset-slot-preview">${t('mastery', 'slotEmpty')}</span>`
      return `<button class="modal-btn modal-btn--ghost mastery-preset-slot-btn" data-slot="${i}" data-sfx="modal"${disabledAttr}><span class="mastery-preset-slot-label">${label}</span>${preview}</button>`
    }).join('')
    const blockedHtml = blocked ? `<div class="mastery-preset-error">${t('mastery', 'saveNoHistory')}</div>` : ''
    return `
      <div class="modal-panel mastery-preset-panel" role="dialog" aria-modal="true" aria-labelledby="preset-save-title">
        <button class="modal-close-btn" data-action="close" aria-label="${t('settings', 'close')}"></button>
        <h2 class="modal-title" id="preset-save-title">${t('mastery', 'savePlan')}</h2>
        ${blockedHtml}
        <div class="mastery-preset-slots">${slots}</div>
        <button class="modal-btn modal-btn--ghost" data-action="copy" data-sfx="modal"${disabledAttr}>${t('mastery', 'copyClipboard')}</button>
      </div>
    `
  }

  backdrop.innerHTML = buildHtml()
  playSound('modal.open')
  parent.appendChild(backdrop)

  const dismiss = (): void => { playSound('modal.close'); backdrop.remove(); onClose() }
  backdrop.querySelector<HTMLButtonElement>('[data-action="close"]')!.addEventListener('click', dismiss)
  backdrop.addEventListener('click', e => { if (e.target === backdrop) dismiss() })

  backdrop.querySelectorAll<HTMLButtonElement>('[data-slot]').forEach(btn => {
    btn.addEventListener('click', () => {
      if (currentPlan === null) return
      const i = parseInt(btn.dataset['slot']!, 10) as 0 | 1 | 2
      presets[i] = currentPlan
      setPref('masteryPresets', presets)
      dismiss()
    })
  })

  backdrop.querySelector<HTMLButtonElement>('[data-action="copy"]')!.addEventListener('click', () => {
    if (currentPlan === null) return
    navigator.clipboard?.writeText(currentPlan).catch(() => { /* ignore */ })
    dismiss()
  })

  return () => backdrop.remove()
}

// ── Preset Load Modal ──────────────────────────────────────────────────────

export function mountMasteryPresetLoadModal(
  parent: HTMLElement,
  onLoad: (plan: string) => void,
  onClose: () => void,
): () => void {
  const prefs = getPrefs()
  const presets: [string?, string?, string?] = [...(prefs.masteryPresets ?? [undefined, undefined, undefined])] as [string?, string?, string?]

  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop mastery-node-backdrop'

  const slots = ([0, 1, 2] as const).map(i => {
    const saved = presets[i]
    const label = t('mastery', 'slot').replace('{n}', String(i + 1))
    const preview = saved ? `<span class="mastery-preset-slot-preview">${truncatePlan(saved)}</span>` : `<span class="mastery-preset-slot-preview">${t('mastery', 'slotEmpty')}</span>`
    const disabled = saved ? '' : ' disabled'
    return `<button class="modal-btn modal-btn--ghost mastery-preset-slot-btn" data-slot="${i}" data-sfx="modal"${disabled}><span class="mastery-preset-slot-label">${label}</span>${preview}</button>`
  }).join('')

  backdrop.innerHTML = `
    <div class="modal-panel mastery-preset-panel" role="dialog" aria-modal="true" aria-labelledby="preset-load-title">
      <button class="modal-close-btn" data-action="close" aria-label="${t('settings', 'close')}"></button>
      <h2 class="modal-title" id="preset-load-title">${t('mastery', 'loadPlan')}</h2>
      <div class="mastery-preset-slots">${slots}</div>
      <div class="mastery-preset-paste-row">
        <input class="mastery-preset-paste-input" type="text" placeholder="FD1, FB1, …" />
        <button class="modal-btn modal-btn--primary mastery-plan-btn" data-action="import" data-sfx="modal">${t('mastery', 'importCode')}</button>
      </div>
      <div class="mastery-preset-error" aria-live="polite"></div>
    </div>
  `
  playSound('modal.open')
  parent.appendChild(backdrop)

  const dismiss = (): void => { playSound('modal.close'); backdrop.remove(); onClose() }
  const errorEl = backdrop.querySelector<HTMLElement>('.mastery-preset-error')!
  backdrop.querySelector<HTMLButtonElement>('[data-action="close"]')!.addEventListener('click', dismiss)
  backdrop.addEventListener('click', e => { if (e.target === backdrop) dismiss() })

  backdrop.querySelectorAll<HTMLButtonElement>('[data-slot]').forEach(btn => {
    btn.addEventListener('click', () => {
      const i = parseInt(btn.dataset['slot']!, 10) as 0 | 1 | 2
      const plan = presets[i]
      if (plan) { onLoad(plan); dismiss() }
    })
  })

  const importBtn = backdrop.querySelector<HTMLButtonElement>('[data-action="import"]')!
  const pasteInput = backdrop.querySelector<HTMLInputElement>('.mastery-preset-paste-input')!

  const tryImport = (): void => {
    const val = pasteInput.value.trim()
    if (!val) return
    const parsed = parseMasterySaveString(val)
    if (!parsed) {
      errorEl.textContent = t('mastery', 'planInvalid')
      return
    }
    onLoad(val)
    dismiss()
  }

  importBtn.addEventListener('click', tryImport)
  pasteInput.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); tryImport() } })

  return () => backdrop.remove()
}

// ── Mastery Modal (main list) ──────────────────────────────────────────────

export function mountMasteryModal(
  parent: HTMLElement,
  masteryProgress: Partial<Record<MasteryId, MasteryProgress>>,
  onClose: () => void,
  onAssign: (id: MasteryId, treeIdx: number, nodeIdx: number) => void,
  onDump: (id: MasteryId, count: number) => void,
  onReset: (id: MasteryId) => void,
  getPendingGains: () => Array<{ id: MasteryId; xpGain: number }>,
  ascentCount: number,
  freeMasteryPointsUsed: Partial<Record<MasteryId, number>>,
  masteryDumpPoints: Partial<Record<MasteryId, number>>,
  getRemainingFreePoints: (id: MasteryId) => number,
  onDie: () => void,
  // "Rebirth" while the freeRebirth relic is armed, else "Die and rebirth".
  dieLabel: string = t('game', 'dieRebirth'),
  // Seen-points tracking: a mastery's unused points only show a notif dot
  // until its tree modal has been viewed at that point count.
  getSeenPoints: (id: MasteryId) => number = () => 0,
  onTreeSeen: (id: MasteryId) => void = () => {},
): () => void {
  // Active plan: persisted in prefs, reset on ascent.
  let activePlan: string | null = getPrefs().activeMasteryPlan ?? null

  const backdrop = document.createElement('div')
  backdrop.className = 'modal-backdrop'
  backdrop.innerHTML = `
    <div class="modal-panel mastery-panel" role="dialog" aria-modal="true" aria-labelledby="mastery-title">
      <button class="modal-close-btn" data-action="close" aria-label="${t('settings', 'close')}"></button>
      <h2 class="modal-title" id="mastery-title">${t('mastery', 'title')}</h2>
      <div class="mastery-plan-actions">
        <button class="modal-btn modal-btn--ghost mastery-plan-btn" data-action="save-plan" data-sfx="modal">${t('mastery', 'savePlan')}</button>
        <button class="modal-btn modal-btn--ghost mastery-plan-btn" data-action="load-plan" data-sfx="modal">${t('mastery', 'loadPlan')}</button>
        <button class="modal-btn modal-btn--primary mastery-plan-btn" data-action="assign-all" data-sfx="modal">${t('mastery', 'assignAll')}</button>
        <label class="mastery-dump-toggle" title="${t('mastery', 'assignAllDumpTooltip')}">
          <input type="checkbox" class="mastery-dump-toggle-input" data-action="assign-all-dump">
          <span>${t('mastery', 'assignAllDump')}</span>
        </label>
        <span class="mastery-plan-active" hidden></span>
      </div>
      <div class="mastery-categories"></div>
      <div class="mastery-die-footer">
        <p class="mastery-die-note">${t('mastery', 'pendingGainsNote')}</p>
        <button class="modal-btn modal-btn--danger mastery-die-btn" data-action="die">${dieLabel}</button>
      </div>
    </div>
  `
  playSound('modal.open')
  parent.appendChild(backdrop)

  const categoriesEl = backdrop.querySelector<HTMLElement>('.mastery-categories')!
  const planActiveEl = backdrop.querySelector<HTMLElement>('.mastery-plan-active')!
  const assignAllBtn = backdrop.querySelector<HTMLButtonElement>('[data-action="assign-all"]')!

  function refreshPlanUI(): void {
    if (activePlan) {
      planActiveEl.textContent = truncatePlan(activePlan)
      planActiveEl.hidden = false
      assignAllBtn.disabled = false
    } else {
      planActiveEl.hidden = true
      assignAllBtn.disabled = true
    }
  }
  refreshPlanUI()

  let subCleanup: (() => void) | null = null
  function closeSub(): void { if (subCleanup) { subCleanup(); subCleanup = null } }

  interface RowState {
    oldPct: number
    gainPct: number
    levelsGained: number
    displayLevel: number
    pts: number
  }

  function computeVisibleRows(): Array<{ catLabel: string; id: MasteryId; label: string; state: RowState }> {
    const gainById = new Map(getPendingGains().map(g => [g.id, g.xpGain]))
    const out: Array<{ catLabel: string; id: MasteryId; label: string; state: RowState }> = []
    for (const cat of masteryCategories) {
      for (const m of cat.masteries) {
        const p = prog(masteryProgress, m.id)
        const xpGain = gainById.get(m.id) ?? 0
        if (m.id !== 'enemy' && m.id !== 'action' && p.level === 1 && p.xp === 0 && xpGain === 0) continue
        // Mastery is visible — include free points in available count
        const freeUsedHere = freeMasteryPointsUsed[m.id] ?? 0
        const pts = masteryPointsAvailable(p, freeUsedHere, masteryDumpPoints[m.id] ?? 0) + getRemainingFreePoints(m.id)
        let oldPct: number, gainPct: number, levelsGained: number
        if (m.id === 'enemy' && xpGain > 0) {
          // xpGain is the new absolute level (sentinel for non-XP enemy mastery)
          levelsGained = Math.max(0, xpGain - p.level)
          oldPct = 0; gainPct = levelsGained > 0 ? 1 : 0
        } else if (xpGain > 0) {
          const pv = previewMasteryGain(p.xp, p.level, xpGain, m.id)
          oldPct = pv.oldPct; gainPct = pv.gainPct
          levelsGained = pv.levelsGained
        } else {
          // Stored progress is in natural units (< requirement); clamp anyway as a
          // guard for legacy saves whose banked XP predates the natural-unit storage.
          oldPct = Math.min(100, Math.round((p.xp / masteryXpNeeded(p.level, m.id)) * 100))
          gainPct = 0; levelsGained = 0
        }
        out.push({ catLabel: getMasteryCategoryLabel(cat.id), id: m.id, label: getMasteryLabel(m.id), state: { oldPct, gainPct, levelsGained, displayLevel: p.level, pts } })
      }
    }
    return out
  }

  function applyRowState(rowEl: HTMLElement, label: string, s: RowState, id: MasteryId): void {
    const btnLabel = rowEl.querySelector<HTMLElement>('.mastery-name-label')!
    btnLabel.textContent = label
    const dot = rowEl.querySelector<HTMLElement>('.mastery-name-btn .notif-dot')!
    // Unused points only light the dot until the tree modal has been seen at
    // this exact point count (new points re-light it).
    dot.hidden = s.pts === 0 || s.pts === getSeenPoints(id)
    const barWrap = rowEl.querySelector<HTMLElement>('.mastery-bar-wrap')!
    barWrap.innerHTML = renderMasteryBar(s.oldPct, s.gainPct)
    const levelEl = rowEl.querySelector<HTMLElement>('.mastery-level')!
    const levelClasses = ['mastery-level']
    if (s.levelsGained > 0) levelClasses.push('mastery-level--gain')
    levelEl.className = levelClasses.join(' ')
    levelEl.innerHTML = `Lv.${s.displayLevel}${s.pts > 0 ? ` · <span class="mastery-pts">${s.pts}pt</span>` : ''}`
    const badge = rowEl.querySelector<HTMLElement>('.mastery-gain-badge')!
    if (s.levelsGained > 0) {
      badge.innerHTML = `+${s.levelsGained}`
      badge.className = 'mastery-gain-badge'
      badge.hidden = false
    } else { badge.hidden = true }
  }

  function wireRowButton(rowEl: HTMLElement): void {
    const btn = rowEl.querySelector<HTMLButtonElement>('.mastery-name-btn')!
    btn.addEventListener('click', () => {
      const id = btn.dataset['mastery'] as MasteryId
      const def = allMasteries.find(m => m.id === id)!
      closeSub()
      // The tree modal stays open across assigns/resets, so we must NOT clear
      // subCleanup in those callbacks — only when the tree modal itself closes.
      subCleanup = mountMasteryTreeModal(
        parent, def, masteryProgress, freeMasteryPointsUsed, masteryDumpPoints, () => getRemainingFreePoints(id), ascentCount,
        (treeIdx, nodeIdx) => { onAssign(id, treeIdx, nodeIdx); onTreeSeen(id); buildRows() },
        (count) => { onDump(id, count); onTreeSeen(id); buildRows() },
        () => { onReset(id); onTreeSeen(id); buildRows() },
        () => { subCleanup = null },
      )
      // Viewing the tree marks its current points as seen — the row dot (and
      // the top-bar masteries dot, via the callback) clear until new points.
      onTreeSeen(id)
      buildRows()
    })
  }

  // Full rebuild — replaces all category DOM. Triggered on first mount and when
  // the visible-mastery set changes (a new mastery becomes eligible).
  function buildRows(): void {
    const visible = computeVisibleRows()
    const byCategory = new Map<string, typeof visible>()
    for (const r of visible) {
      const list = byCategory.get(r.catLabel) ?? []
      list.push(r)
      byCategory.set(r.catLabel, list)
    }

    categoriesEl.innerHTML = ''
    for (const [catLabel, rows] of byCategory) {
      const catEl = document.createElement('div')
      catEl.className = 'mastery-category'
      const labelEl = document.createElement('div')
      labelEl.className = 'mastery-category-label'
      labelEl.textContent = catLabel
      catEl.appendChild(labelEl)
      for (const r of rows) {
        const rowEl = document.createElement('div')
        rowEl.className = 'mastery-row'
        rowEl.dataset['masteryId'] = r.id
        rowEl.innerHTML = `
          <button class="mastery-name-btn" data-mastery="${r.id}" data-sfx="modal">
            <span class="mastery-name-label"></span>
            <span class="notif-dot" hidden></span>
          </button>
          <div class="mastery-bar-wrap"></div>
          <span class="mastery-level"></span>
          <span class="mastery-gain-badge" hidden></span>
        `
        applyRowState(rowEl, r.label, r.state, r.id)
        wireRowButton(rowEl)
        catEl.appendChild(rowEl)
      }
      categoriesEl.appendChild(catEl)
    }
  }

  // In-place refresh — only touches text/bar widths/hidden state, preserves
  // existing buttons (and their click handlers) so a click in flight isn't
  // dropped when the interval fires between mousedown and mouseup.
  function refreshRows(): void {
    const visible = computeVisibleRows()
    const newIds = visible.map(r => r.id)
    const currentRowEls = Array.from(categoriesEl.querySelectorAll<HTMLElement>('[data-mastery-id]'))
    const currentIds = currentRowEls.map(el => el.dataset['masteryId'] as MasteryId)
    const sameStructure = currentIds.length === newIds.length
      && currentIds.every((id, i) => id === newIds[i])
    if (!sameStructure) { buildRows(); return }
    for (let i = 0; i < visible.length; i++) {
      applyRowState(currentRowEls[i], visible[i].label, visible[i].state, visible[i].id)
    }
  }

  buildRows()
  // Keep the gain preview fresh while the modal is open: XP keeps accruing
  // in the background, so refresh every 500 ms unless a sub-modal is open
  // (its own state is independent of these top-level rows).
  const refreshTimer = window.setInterval(() => { if (!subCleanup) refreshRows() }, 500)

  // Plan action buttons
  backdrop.querySelector<HTMLButtonElement>('[data-action="save-plan"]')!.addEventListener('click', () => {
    closeSub()
    const current = generateMasterySaveString(masteryProgress)
    subCleanup = mountMasteryPresetSaveModal(parent, current, () => { subCleanup = null })
  })

  backdrop.querySelector<HTMLButtonElement>('[data-action="load-plan"]')!.addEventListener('click', () => {
    closeSub()
    subCleanup = mountMasteryPresetLoadModal(parent, (plan) => {
      activePlan = plan
      setPref('activeMasteryPlan', plan)
      refreshPlanUI()
    }, () => { subCleanup = null })
  })

  const dumpExtrasToggle = backdrop.querySelector<HTMLInputElement>('[data-action="assign-all-dump"]')!

  assignAllBtn.addEventListener('click', () => {
    if (!activePlan) return
    const plan = parseMasterySaveString(activePlan)
    if (!plan) return
    // When checked, leftover points beyond the plan are dumped (legacy behavior);
    // when unchecked (default), they remain unassigned.
    const dumpExtras = dumpExtrasToggle.checked
    for (const [masteryId, entries] of plan) {
      const masteryDef = allMasteries.find(m => m.id === masteryId)
      if (!masteryDef) continue
      let pointer = 0
      let madeProgress = true
      while (madeProgress && pointer < entries.length) {
        madeProgress = false
        const entry = entries[pointer]
        const treeDef = masteryDef.trees.find(tr => tr.index === entry.treeIdx)
        if (!treeDef) { pointer++; madeProgress = true; continue }
        const p = prog(masteryProgress, masteryId)
        const freeUsedHere = freeMasteryPointsUsed[masteryId] ?? 0
        const dumped = masteryDumpPoints[masteryId] ?? 0
        const available = masteryPointsAvailable(p, freeUsedHere, dumped) + getRemainingFreePoints(masteryId)
        const result = computeAssignPath(p, treeDef, entry.treeIdx, entry.nodeIdx)
        if (!Array.isArray(result)) {
          pointer++; madeProgress = true; continue  // structural block — skip
        }
        if (result.length === 0) {
          pointer++; madeProgress = true; continue  // already assigned
        }
        const cost = result.reduce((s, idx) => s + nodeCost(idx), 0)
        if (available >= cost) {
          for (const nIdx of result) onAssign(masteryId, entry.treeIdx, nIdx)
          pointer++; madeProgress = true
        }
        // else: can't afford — stop for this mastery
      }
      // Dump remaining free points for this mastery after plan is done — only when
      // the toggle is checked. Otherwise leftover points stay unassigned.
      if (dumpExtras) {
        const freshP = prog(masteryProgress, masteryId)
        const remaining = masteryPointsAvailable(freshP, freeMasteryPointsUsed[masteryId] ?? 0, masteryDumpPoints[masteryId] ?? 0) + getRemainingFreePoints(masteryId)
        if (remaining > 0) onDump(masteryId, remaining)
      }
    }
    buildRows()
  })

  const dismiss = (): void => { playSound('modal.close'); window.clearInterval(refreshTimer); closeSub(); backdrop.remove(); onClose() }
  backdrop.querySelector<HTMLButtonElement>('[data-action="close"]')!.addEventListener('click', dismiss)
  backdrop.addEventListener('click', e => { if (e.target === backdrop) dismiss() })

  backdrop.querySelector<HTMLButtonElement>('[data-action="die"]')!
    .addEventListener('click', () => { dismiss(); onDie() })

  return () => { window.clearInterval(refreshTimer); closeSub(); backdrop.remove() }
}
